import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  useApproveAllReturnsMutation, 
  useRejectAllReturnsMutation
} from '../../../../redux/apis/endpoints/fulfillmentGroupApi';
import { useGetAddressesQuery, useGetCurrentUserQuery } from '../../../../redux/apis';
import { useConfirmFundCollectionMutation } from '../../../../redux/apis/endpoints/paymentApi';
import { estimateReturnShippingForFG } from '../../../../utils/returnShippingEstimator';
import { formatCurrency } from '../../../../utils/formatters';

export const useReturnProcessing = (fulfillmentGroupId, entity, pendingReturnItems) => {
  const history = useHistory();
  
  // Component state
  const [decision, setDecision] = useState('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [usageFee, setUsageFee] = useState('');
  const [selectedReturnAddress, setSelectedReturnAddress] = useState(null);
  const [refundFundPaymentCompleted, setRefundFundPaymentCompleted] = useState(false);
  const [currentPaymentIntent, setCurrentPaymentIntent] = useState(null);
  const [estimatedReturnShipping, setEstimatedReturnShipping] = useState(5.99);
  const [isLoadingShipping, setIsLoadingShipping] = useState(false);
  const [customerAddress, setCustomerAddress] = useState(null);
  
  // User data
  const { data: user } = useGetCurrentUserQuery();
  
  // Get merchant's addresses for return shipping
  const { 
    data: merchantAddresses = [], 
    isLoading: isLoadingAddresses,
    refetch: refetchAddresses
  } = useGetAddressesQuery();
  
  const [approveAllReturns, { isLoading: isApproving }] = useApproveAllReturnsMutation();
  const [rejectAllReturns, { isLoading: isRejecting }] = useRejectAllReturnsMutation();
  const [confirmFundCollection] = useConfirmFundCollectionMutation();
  
  // Derived state
  const currency = pendingReturnItems[0]?.currency || 'USD';
  const anyLoading = isApproving || isRejecting || isLoadingAddresses;
  
  // Calculate customer refund: item price - estimated shipping - usage fee
  const calculatedRefund = useMemo(() => {
    if (pendingReturnItems.length === 0) return 0;
    
    // Sum the total value using item.price (from order-item) instead of item.amount
    const totalItemValue = pendingReturnItems.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = item.quantity || 1;
      return sum + (itemPrice * itemQuantity);
    }, 0);
    
    // Calculate refund: items - shipping - usage fee (customer pays shipping)
    const refund = totalItemValue - estimatedReturnShipping - (parseFloat(usageFee) || 0);
    return refund; // Allow negative values to be shown in UI for warning
  }, [pendingReturnItems, estimatedReturnShipping, usageFee]);

  // Calculate refund amount merchant needs to pay (item price - usage fee, no shipping)
  const refundAmountToPay = useMemo(() => {
    if (pendingReturnItems.length === 0) return 0;
    
    // Sum the total value using item.price (from order-item)
    const totalItemValue = pendingReturnItems.reduce((sum, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = item.quantity || 1;
      return sum + (itemPrice * itemQuantity);
    }, 0);
    
    // Merchant pays: item value - usage fee (customer pays return shipping separately)
    return totalItemValue - (parseFloat(usageFee) || 0);
  }, [pendingReturnItems, usageFee]);

  // Get customer delivery address for shipping estimation from fulfillment group's order's shipping address
  useEffect(() => {
    if (entity?.order?.shippingAddress) {
      setCustomerAddress(entity.order.shippingAddress);
    }
  }, [entity]);

  // Initialize usage fee and payment status from existing request data
  useEffect(() => {
    if (pendingReturnItems && pendingReturnItems.length > 0) {
      const firstRequest = pendingReturnItems[0];
      
      // Check if payment has been completed
      if (firstRequest.fundCollectionPayment) {
        console.log('Found existing fund collection payment, marking as completed');
        setRefundFundPaymentCompleted(true);
        
        // Load saved usage fee if available
        if (firstRequest.usageFee !== undefined && firstRequest.usageFee !== null) {
          console.log('Loading saved usage fee:', firstRequest.usageFee);
          setUsageFee(firstRequest.usageFee.toString());
        }
      }
    }
  }, [pendingReturnItems]);

  // Estimate return shipping cost when addresses are available
  useEffect(() => {
    const estimateShipping = async () => {
      if (!customerAddress || !selectedReturnAddress || !entity) {
        return;
      }
      
      setIsLoadingShipping(true);
      try {
        const estimatedCost = await estimateReturnShippingForFG(
          entity,
          customerAddress,
          selectedReturnAddress
        );
        setEstimatedReturnShipping(estimatedCost);
      } catch (error) {
        console.error('Error estimating shipping:', error);
        setEstimatedReturnShipping(5.99); // Fallback
      } finally {
        setIsLoadingShipping(false);
      }
    };

    estimateShipping();
  }, [customerAddress, selectedReturnAddress, entity]);

  // Handle address selection
  const handleAddressChange = (e) => {
    if (!e || !e.target) return;
    
    const selectedAddressId = e.target.value;
    // Let the AddressDropdown component handle the 'new' case with its built-in modal
    if (selectedAddressId === 'new') {
      return; // The modal will be shown by the AddressDropdown component
    }
    
    // Find the selected address from our addresses array
    const address = merchantAddresses.find(addr => addr.id === selectedAddressId);
    if (address) {
      setSelectedReturnAddress(address);
    }
  };

  // Handle new address creation success
  const handleAddressSuccess = (newAddress) => {
    // Set the newly created address as selected
    setSelectedReturnAddress(newAddress);
    // Refresh the addresses list
    refetchAddresses();
  };
  
  // Handle decision changes
  const handleDecisionChange = (newDecision) => {
    setDecision(newDecision);
    // Reset payment completion when changing decision
    if (newDecision !== 'approve') {
      setRefundFundPaymentCompleted(false);
    }
  };
  
  // Proceed with return approval (when refund payment is completed)
  const proceedWithReturnApproval = async () => {
    const payload = {
      fulfillmentGroupId: entity.id,
      approvalComments: `Usage fee: ${usageFee || '0'}`,
      usageFee: parseFloat(usageFee) || 0,
      returnToAddress: selectedReturnAddress?.id
    };
    
    console.log("Approving return with payload:", payload);
    
    try {
      const result = await approveAllReturns(payload).unwrap();
      console.log("Return approval result:", result);
      toast.success(
        `All ${pendingReturnItems.length} return request${pendingReturnItems.length !== 1 ? 's' : ''} approved.`
      );
      history.push(`/dashboard/transactions/fg/${fulfillmentGroupId}`);
    } catch (error) {
      console.error('[ProcessReturnPage] Error in approveAllReturns:', error);
      toast.error(`Error approving returns: ${error.message || 'Unknown error'}`);
    }
  };

  // Process returns (approve or reject)
  const executeDecision = async () => {
    if (pendingReturnItems.length === 0) return;

    // Check for negative refund (prevent customer owing money)
    if (decision === 'approve' && calculatedRefund < 0) {
      toast.error('Customer refund cannot be negative. Please reduce usage fee or reject the return.');
      return;
    }

    try {
      if (decision === 'approve') {
        // Check if a return address is selected when approving
        if (!selectedReturnAddress && merchantAddresses.length > 0) {
          toast.error('Please select a return address');
          return;
        }
        
        // Check if refund fund payment has been completed
        if (!refundFundPaymentCompleted) {
          toast.error('Please complete the refund payment above to proceed with approval');
          return;
        }
        
        // Proceed with approval now that payment is completed
        await proceedWithReturnApproval();
        
      } else if (decision === 'reject') {
        // Check if payment was made and warn the user about automatic refund
        if (refundFundPaymentCompleted && refundAmountToPay > 0) {
          const confirmReject = window.confirm(
            `You have already paid ${formatCurrency(refundAmountToPay, currency)} for this return request. ` +
            `If you reject this return, this amount will be automatically refunded to you. ` +
            `Are you sure you want to reject this return?`
          );
          
          if (!confirmReject) {
            return; // User cancelled the rejection
          }
        }
        
        const payload = {
          fulfillmentGroupId: entity.id,
          rejectionReason: rejectionReason || 'All returns rejected via bulk action'
        };
        
        try {
          const result = await rejectAllReturns(payload).unwrap();
          console.log("Return rejection result:", result);
          
          // Handle refund results if present
          if (result.refundResults && result.refundResults.processed > 0) {
            const { successful, failed, processed } = result.refundResults;
            
            if (successful > 0 && failed === 0) {
              toast.success(
                `All ${pendingReturnItems.length} return request${pendingReturnItems.length !== 1 ? 's' : ''} rejected. ` +
                `Your payment${successful > 1 ? 's' : ''} ha${successful > 1 ? 've' : 's'} been refunded.`
              );
            } else if (successful > 0 && failed > 0) {
              toast.success(
                `All ${pendingReturnItems.length} return request${pendingReturnItems.length !== 1 ? 's' : ''} rejected. ` +
                `${successful} refund${successful > 1 ? 's' : ''} processed successfully, ${failed} failed.`
              );
            } else if (failed > 0) {
              toast.error(
                `Returns rejected but failed to refund your payment${failed > 1 ? 's' : ''}. Please contact support.`
              );
            }
          } else {
            toast.success(
              `All ${pendingReturnItems.length} return request${pendingReturnItems.length !== 1 ? 's' : ''} rejected.`
            );
          }
          
          history.push(`/dashboard/transactions/fg/${fulfillmentGroupId}`);
        } catch (error) {
          console.error('[ProcessReturnPage] Error in rejectAllReturns:', error);
          toast.error(`Error rejecting returns: ${error.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('[ProcessReturnPage] Unexpected error:', error);
      toast.error('Error processing returns. Please try again.');
    }
  };

  // Handle successful refund fund payment
  const handleFundCollectionSuccess = async (paymentData) => {
    console.log('Fund collection success:', paymentData);
    try {
      // Confirm the payment with the backend
      const confirmResult = await confirmFundCollection({
        paymentIntentId: paymentData.paymentIntentId,
        fulfillmentGroupId: fulfillmentGroupId,
        usageFee: parseFloat(usageFee) || 0,
        returnToAddress: selectedReturnAddress?.id
      }).unwrap();
      
      console.log('Fund collection confirmed:', confirmResult);
      setRefundFundPaymentCompleted(true);
      setCurrentPaymentIntent(null);
      
    } catch (error) {
      console.error('Error confirming fund collection:', error);
    }
  };

  // Handle refund fund payment error
  const handleFundCollectionError = (error) => {
    console.error('Refund fund payment error:', error);
    toast.error('Refund payment failed. Please try again.');
  };

  return {
    // State
    decision,
    rejectionReason,
    usageFee,
    selectedReturnAddress,
    refundFundPaymentCompleted,
    currentPaymentIntent,
    estimatedReturnShipping,
    isLoadingShipping,
    customerAddress,
    
    // Derived state
    currency,
    anyLoading,
    calculatedRefund,
    refundAmountToPay,
    
    // Data
    merchantAddresses,
    isLoadingAddresses,
    
    // Handlers
    handleAddressChange,
    handleAddressSuccess,
    handleDecisionChange,
    executeDecision,
    handleFundCollectionSuccess,
    handleFundCollectionError,
    
    // Setters for components that need direct control
    setRejectionReason,
    setUsageFee,
    setCurrentPaymentIntent,
    
    // Utility functions
    proceedWithReturnApproval
  };
}; 